import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  FORM_THEMES, Pill, FormHeader, EnhancedShutdownInfo, PhotoGrid,
  ErrorBanner, FormBottomBar, captureGPS as doGPS, processPhoto
} from "./FormShared";

const theme = FORM_THEMES.dtm;
const OLD_METER_MAKES = ["L&T", "HPL", "Secure", "Genus", "Other"];

const PRIMARY_CAPACITIES = [16, 25, 63, 100];
const EXTENDED_CAPACITIES = [160, 250, 400, 630];

const CABLE_CONFIG = {
  16: { disabled: true, types: [], sizes: {} },
  25: { disabled: false, types: ["ABC"], sizes: { "ABC": [50] }, defaultType: "ABC", defaultSize: 50 },
  63: { disabled: false, types: ["ABC"], sizes: { "ABC": [70] }, defaultType: "ABC", defaultSize: 70 },
  100: { disabled: false, types: ["ABC", "1-Core"], sizes: { "ABC": [120], "1-Core": [100] }, defaultType: "ABC", defaultSize: 120 },
  160: { disabled: false, types: ["ABC", "1-Core"], sizes: { "ABC": [120], "1-Core": [100] }, defaultType: "ABC", defaultSize: 120 },
  250: { disabled: false, types: ["1-Core"], sizes: { "1-Core": [400] }, defaultType: "1-Core", defaultSize: 400 },
  400: { disabled: false, types: ["1-Core"], sizes: { "1-Core": [400, 630] }, defaultType: "1-Core", defaultSize: 400 },
  630: { disabled: false, types: ["1-Core"], sizes: { "1-Core": [630, 850] }, defaultType: "1-Core", defaultSize: 630 }
};

export default function DTMForm() {
  const { dtId } = useParams();
  const navigate = useNavigate();
  const [dt, setDT] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [shutdowns, setShutdowns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("pending");
  const [gpsData, setGpsData] = useState({ lat: "", lng: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [showMoreCapacity, setShowMoreCapacity] = useState(false);
  const [form, setForm] = useState({
    meter_serial: "",
    old_meter_available: false,
    old_meter_serial: "", old_meter_reading: "", old_meter_make: "",
    transformer_capacity_kva: "",
    cable_installed: true,
    cable_type: "", cable_size_sqmm: "", cable_length_m: "",
    photos: [null, null, null],
  });

  const photoLabels = ["Mounting", "Old Meter", "Cable"];

  useEffect(() => { api.get("/auth/me").then(r => setCurrentUser(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dtRes = await api.get(`/dt-master/${dtId}`);
        setDT(dtRes.data);
        const shutRes = await api.get("/shutdowns/available", { params: { feeder_code: dtRes.data.feeder_code } });
        setShutdowns(shutRes.data.items || []);
        // Fetch survey to get defaults
        try {
          const surveyRes = await api.get(`/installation/survey/${dtId}`);
          const s = surveyRes.data;
          setSurveyData(s);
          setForm(f => ({
            ...f,
            transformer_capacity_kva: s.transformer_capacity_kva?.toString() || dtRes.data.transformer_capacity_kva?.toString() || "",
            cable_installed: s.cable_type ? true : (s.cable_size_sqmm ? true : true),
            cable_type: s.cable_type || "",
            cable_size_sqmm: s.cable_size_sqmm?.toString() || "",
            cable_length_m: s.cable_length_m?.toString() || "",
          }));
          // Show extended capacities if survey has one
          if (EXTENDED_CAPACITIES.includes(parseInt(s.transformer_capacity_kva))) {
            setShowMoreCapacity(true);
          }
        } catch {
          // No survey record — use DT master capacity if available
          if (dtRes.data.transformer_capacity_kva) {
            setForm(f => ({ ...f, transformer_capacity_kva: dtRes.data.transformer_capacity_kva.toString() }));
            if (EXTENDED_CAPACITIES.includes(parseInt(dtRes.data.transformer_capacity_kva))) {
              setShowMoreCapacity(true);
            }
          }
        }
      } catch { navigate("/dt-master"); }
    };
    fetchData();
  }, [dtId, navigate]);

  // Auto-select cable type/size when capacity changes (same logic as Survey)
  useEffect(() => {
    const capacity = parseInt(form.transformer_capacity_kva);
    if (!capacity || !CABLE_CONFIG[capacity]) return;
    const config = CABLE_CONFIG[capacity];
    if (config.disabled) {
      setForm(f => ({ ...f, cable_installed: false, cable_type: "", cable_size_sqmm: "", cable_length_m: "" }));
    } else if (form.cable_installed) {
      // Only auto-set if current values don't match any valid option for this capacity
      const validTypes = config.types || [];
      if (!validTypes.includes(form.cable_type)) {
        const newType = validTypes.length === 1 ? validTypes[0] : (config.defaultType || "");
        const sizesForType = config.sizes?.[newType] || [];
        const newSize = sizesForType.length === 1 ? sizesForType[0].toString() : (config.defaultSize?.toString() || "");
        setForm(f => ({ ...f, cable_type: newType, cable_size_sqmm: newSize }));
      }
    }
  }, [form.transformer_capacity_kva]);

  const captureGPS = useCallback((silent = false) => doGPS(setGpsStatus, setGpsData, silent, toast), []);
  useEffect(() => { captureGPS(true); }, [captureGPS]);

  const handlePhotoUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const metadata = {
      capturedAt: new Date().toISOString(),
      capturedDate: new Date().toLocaleDateString("en-IN"),
      capturedTime: new Date().toLocaleTimeString("en-IN"),
      gps_lat: gpsData.lat || "N/A", gps_lng: gpsData.lng || "N/A",
      technicianName: currentUser?.full_name || "Unknown",
      label: `DTM ${photoLabels[index]}`
    };
    const photoData = await processPhoto(file, metadata);
    const newPhotos = [...form.photos]; newPhotos[index] = photoData;
    setForm(f => ({ ...f, photos: newPhotos }));
    toast.success(`${photoLabels[index]} photo captured`);
  };

  const removePhoto = (index) => {
    const p = [...form.photos]; p[index] = null;
    setForm(f => ({ ...f, photos: p }));
  };

  const handleSubmit = async () => {
    if (!form.meter_serial) { toast.error("Meter serial number is required"); return; }
    if (!form.transformer_capacity_kva) { toast.error("DT Rating (kVA) is required"); return; }
    if (!gpsData.lat) { toast.error("GPS location not captured. Please enable location services and tap 'Capture GPS'"); return; }
    if (!form.photos.some(p => p)) { toast.error("At least 1 photo is required"); return; }
    if (form.old_meter_available && !form.old_meter_make) { toast.error("Please select old meter make/brand"); return; }

    // Confirmation: Rating change
    if (surveyData) {
      const surveyKva = surveyData.transformer_capacity_kva?.toString() || "";
      if (surveyKva && form.transformer_capacity_kva !== surveyKva) {
        const ok = window.confirm(`Are you sure?\nChange DT Rating from ${surveyKva} kVA to ${form.transformer_capacity_kva} kVA?`);
        if (!ok) return;
      }
    }

    // Confirmation: Cable details change
    if (surveyData && form.cable_installed) {
      const sCableType = surveyData.cable_type || "";
      const sCableSize = surveyData.cable_size_sqmm?.toString() || "";
      const sCableLen = surveyData.cable_length_m?.toString() || "";
      const changed = form.cable_type !== sCableType || form.cable_size_sqmm !== sCableSize || form.cable_length_m !== sCableLen;
      if (changed && (sCableType || sCableSize || sCableLen)) {
        const fromStr = `${sCableType || "—"}/${sCableSize || "—"}mm²/${sCableLen || "—"}mtr`;
        const toStr = `${form.cable_type || "—"}/${form.cable_size_sqmm || "—"}mm²/${form.cable_length_m || "—"}mtr`;
        const ok = window.confirm(`Are you sure?\nChange Cable details from ${fromStr} to ${toStr}?`);
        if (!ok) return;
      }
    }

    setLoading(true); setError(null);
    try {
      const photosData = form.photos.filter(p => p).map(p => ({
        url: `/mock/dtm/${Date.now()}_${p.filename}`,
        metadata: { capturedAt: p.capturedAt, gps_lat: p.gps_lat, gps_lng: p.gps_lng, technician: p.technicianName, label: p.label }
      }));
      await api.post("/installation/dtm", {
        dt_id: dtId, meter_serial: form.meter_serial,
        old_meter_serial: form.old_meter_available ? (form.old_meter_serial || null) : null,
        old_meter_reading: form.old_meter_available && form.old_meter_reading ? parseFloat(form.old_meter_reading) : null,
        old_meter_make: form.old_meter_available ? (form.old_meter_make || null) : null,
        transformer_capacity_kva: form.transformer_capacity_kva ? parseFloat(form.transformer_capacity_kva) : null,
        cable_installed: form.cable_installed,
        cable_type: form.cable_installed ? (form.cable_type || null) : null,
        cable_size_sqmm: form.cable_installed && form.cable_size_sqmm ? parseFloat(form.cable_size_sqmm) : null,
        cable_length_m: form.cable_installed && form.cable_length_m ? parseFloat(form.cable_length_m) : null,
        gps_lat: parseFloat(gpsData.lat), gps_lng: parseFloat(gpsData.lng),
        photos: photosData.map(p => p.url), photos_metadata: photosData
      });
      toast.success("DTM completed!");
      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "DTM submission failed";
      setError(msg); toast.error(msg, { duration: 10000 });
    } finally { setLoading(false); }
  };

  const cableConfig = (() => {
    const cap = parseInt(form.transformer_capacity_kva);
    return CABLE_CONFIG[cap] || { disabled: false, types: ["ABC", "1-Core"], sizes: {} };
  })();
  const is16KVA = parseInt(form.transformer_capacity_kva) === 16;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="dtm-form">
      <FormHeader theme={theme} dt={dt} gpsStatus={gpsStatus} gpsData={gpsData}
        onGpsClick={() => captureGPS()} onBack={() => navigate(-1)} />

      {/* Sub-header: technician + shutdown info */}
      <div className="px-3 pt-2 space-y-1.5">
        <div className="text-[10px] text-slate-400">Technician: {currentUser?.full_name}</div>
        <EnhancedShutdownInfo shutdowns={shutdowns} />
      </div>

      {/* Form Body */}
      <div className="flex-1 px-3 pt-3 pb-20 space-y-4">
        {/* Meter Serial */}
        <div className="flex items-center gap-3">
          <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">Meter No *</label>
          <input type="text" className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-lg font-mono bg-white"
            placeholder="Scan or enter meter serial" value={form.meter_serial}
            onChange={e => setForm({ ...form, meter_serial: e.target.value })}
            disabled={submitted} data-testid="meter-serial-input" />
        </div>

        {/* Rating (kVA) */}
        <div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">Rating *</label>
            <div className="flex flex-wrap gap-1.5">
              {PRIMARY_CAPACITIES.map(cap => (
                <Pill key={cap} selected={form.transformer_capacity_kva === cap.toString()} color="blue"
                  testId={`capacity-${cap}`}
                  onClick={() => !submitted && setForm({ ...form, transformer_capacity_kva: cap.toString() })}>
                  {cap}
                </Pill>
              ))}
              <Pill selected={showMoreCapacity || EXTENDED_CAPACITIES.includes(parseInt(form.transformer_capacity_kva))}
                color="blue" onClick={() => !submitted && setShowMoreCapacity(!showMoreCapacity)}>
                +
              </Pill>
            </div>
          </div>
          {showMoreCapacity && (
            <div className="flex flex-wrap gap-1.5 pl-[84px] mt-2">
              {EXTENDED_CAPACITIES.map(cap => (
                <Pill key={cap} selected={form.transformer_capacity_kva === cap.toString()} color="blue"
                  testId={`capacity-${cap}`}
                  onClick={() => !submitted && setForm({ ...form, transformer_capacity_kva: cap.toString() })}>
                  {cap}
                </Pill>
              ))}
            </div>
          )}
        </div>

        {/* Old Meter Toggle */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">Old Meter?</label>
          <div className="flex gap-1.5">
            <Pill selected={form.old_meter_available} color="green" testId="old-meter-yes"
              onClick={() => !submitted && setForm({ ...form, old_meter_available: true })}>Yes</Pill>
            <Pill selected={!form.old_meter_available} color="red" testId="old-meter-no"
              onClick={() => !submitted && setForm({ ...form, old_meter_available: false, old_meter_serial: "", old_meter_reading: "", old_meter_make: "" })}>No</Pill>
          </div>
        </div>

        {/* Old Meter Details — conditional */}
        {form.old_meter_available && (
          <div className="ml-[84px] space-y-3 pl-3 border-l-2 border-orange-200">
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-slate-500 shrink-0 w-14">Serial</label>
              <input type="text" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono bg-white"
                placeholder="Old meter serial" value={form.old_meter_serial}
                onChange={e => setForm({ ...form, old_meter_serial: e.target.value })}
                disabled={submitted} data-testid="old-meter-serial-input" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-slate-500 shrink-0 w-14">kWh</label>
              <input type="number" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                placeholder="Reading" value={form.old_meter_reading}
                onChange={e => setForm({ ...form, old_meter_reading: e.target.value })}
                disabled={submitted} data-testid="old-meter-reading-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">Make / Brand *</label>
              <div className="flex flex-wrap gap-1.5">
                {OLD_METER_MAKES.map(make => (
                  <Pill key={make} selected={form.old_meter_make === make} color="purple" testId={`make-${make}`}
                    onClick={() => !submitted && setForm({ ...form, old_meter_make: make })}>{make}</Pill>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cable Installed Y/N */}
        <div className={is16KVA ? "opacity-40 pointer-events-none" : ""}>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">Cable?</label>
            <div className="flex gap-1.5">
              <Pill selected={form.cable_installed} color="green" testId="cable-yes"
                onClick={() => !submitted && setForm({ ...form, cable_installed: true })}>Yes</Pill>
              <Pill selected={!form.cable_installed} color="red" testId="cable-no"
                onClick={() => !submitted && setForm({ ...form, cable_installed: false, cable_type: "", cable_size_sqmm: "", cable_length_m: "" })}>No</Pill>
            </div>
          </div>

          {/* Cable Type + Size + Length */}
          {form.cable_installed && (
            <div className="flex items-start gap-2 mt-3">
              <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px] pt-4">Cable</label>
              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0 mb-1">
                  <label className="text-xs font-bold text-slate-600 uppercase">Type</label>
                  <label className="text-xs font-bold text-slate-600 uppercase whitespace-nowrap">Size mm²</label>
                  <label className="text-xs font-bold text-slate-600 uppercase whitespace-nowrap">Len (mtr)</label>
                </div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0 items-start">
                  <div className="flex gap-1">
                    {(cableConfig.types?.length > 0 ? cableConfig.types : ["ABC", "1-Core"]).map(type => (
                      <Pill key={type} selected={form.cable_type === type} color="purple"
                        testId={`cable-type-${type}`}
                        onClick={() => {
                          if (submitted) return;
                          const sizes = cableConfig.sizes?.[type] || [];
                          const autoSize = sizes.length === 1 ? sizes[0].toString() : "";
                          setForm(f => ({ ...f, cable_type: type, cable_size_sqmm: autoSize }));
                        }}>
                        {type}
                      </Pill>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    {form.cable_type && (cableConfig.sizes?.[form.cable_type] || [50, 70, 100, 120, 400, 630, 850]).map(size => (
                      <Pill key={size} selected={form.cable_size_sqmm === size.toString()} color="blue"
                        testId={`cable-size-${size}`}
                        onClick={() => !submitted && setForm({ ...form, cable_size_sqmm: size.toString() })}>
                        {size}
                      </Pill>
                    ))}
                  </div>
                  <div>
                    {form.cable_type && form.cable_size_sqmm && (
                      <input type="number"
                        className="w-14 px-2 py-1.5 text-xs font-bold border-2 border-slate-200 rounded-full text-center"
                        placeholder="mtr" value={form.cable_length_m}
                        onChange={e => setForm({ ...form, cable_length_m: e.target.value })}
                        disabled={submitted} data-testid="cable-length-input"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Photos */}
        <PhotoGrid photos={form.photos} photoLabels={photoLabels} onUpload={handlePhotoUpload}
          onRemove={removePhoto} submitted={submitted} theme={theme} testIdPrefix="dtm" />

        <ErrorBanner error={error} testId="dtm-error-banner"
          extraHint={error?.includes("not issued to you") ? "The meter serial must be issued to you from inventory first. Check My Inventory or ask your Supervisor." : null} />
      </div>

      <FormBottomBar theme={theme} submitted={submitted} loading={loading}
        disabled={!form.meter_serial || !form.transformer_capacity_kva || !gpsData.lat} onSubmit={handleSubmit} dtId={dtId} navigate={navigate} />
    </div>
  );
}
